import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Lightbox from 'react-images';
import classnames from 'classnames';
import _ from 'lodash';
import { isEdge, isIE } from 'react-device-detect';

import { buttons as Buttons, Spinner } from '@breezeway/brzw-components';
import CloudinaryImage from '../cloudinary_image';
import ConfirmModal from '../../confirm_modal';
import AddPhotoButton from '../add_photo_button';
import * as utils from '../../../utils';
import VirtualizedPhotoList from './virtualized_photo_list';

import './photo_list.scss';

const { getCloudinaryUrl } = utils.image;

const lightboxTheme = {
  // container
  container: { background: 'rgba(0, 0, 0, 0.6)' },
  lightboxOriginalLink: { color: 'white' },
};

class PhotoList extends Component {
  constructor(props) {
    super(props);
    this.closeLightbox = this.closeLightbox.bind(this);
    this.goToNext = this.goToNext.bind(this);
    this.goToPrevious = this.goToPrevious.bind(this);
    this.onClickPhoto = this.onClickPhoto.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.recomputeList = this.recomputeList.bind(this);
    this.renderPhoto = this.renderPhoto.bind(this);
    this.renderPreview = this.renderPreview.bind(this);
    this.renderStyleguideContent = this.renderStyleguideContent.bind(this);
    this.renderVirtualizedList = this.renderVirtualizedList.bind(this);

    this.state = {
      lightboxOpen: false,
      currentPhotoIndex: 0,
    };
  }

  componentDidUpdate(prevProps) {
    const { photos: prevPhotos } = prevProps;
    const { photos } = this.props;

    if (!_.isEqual(prevPhotos, photos)) {
      this.recomputeList();
    }
  }

  onClickPhoto(e, index) {
    _.invoke(e, 'preventDefault');
    _.invoke(e, 'stopPropagation');
    this.setState({ lightboxOpen: true, currentPhotoIndex: index });
  }

  onDelete(photoId) {
    this.closeLightbox();
    this.props.onDelete(photoId);
  }

  recomputeList() {
    if (this.listRef) {
      this.listRef.recompute();
    }
  }

  closeLightbox() {
    this.setState({ ...this.state, lightboxOpen: false });
  }

  goToPrevious() {
    this.setState({ ...this.state, currentPhotoIndex: this.state.currentPhotoIndex - 1 });
  }

  goToNext() {
    this.setState({ ...this.state, currentPhotoIndex: this.state.currentPhotoIndex + 1 });
  }

  loadingPhoto() {
    const { loadingPhotos, photoHeight, photoWidth } = this.props;

    return _.map(loadingPhotos, photo => {
      const photoId = _.get(photo, 'id', null);
      const photoPreview = _.get(photo, 'preview', null);

      if (!photoPreview) {
        return null;
      }
      return (
        <div key={photoId} className="photo-container photo-placeholder">
          <img
            src={photoPreview}
            className="preview-photo photo-placeholder__img"
            key={photoId}
            alt={photoId}
            style={{
              width: photoWidth,
              height: photoHeight,
            }}
          />
          <div className="photo-placeholder__spinner">
            <Spinner />
          </div>
        </div>
      );
    });
  }

  renderVirtualizedList(photos) {
    if (photos.length) {
      return (
        <VirtualizedPhotoList
          collection={photos}
          renderItem={this.renderPhoto}
          ref={listRef => {
            this.listRef = listRef;
          }}
        />
      );
    }
    return null;
  }

  renderEditable(photo) {
    const { editable, onDelete, importedEdit } = this.props;

    if (editable) {
      if (importedEdit) {
        return this.props.importedEdit(photo);
      }

      if (onDelete) {
        return (
          <ConfirmModal
            body="Are you sure you want to delete this photo?"
            confirmButtonText="Delete"
            onConfirm={() => this.onDelete(photo.id)}
            title="Delete photo"
          >
            <Buttons.TextButton
              autoWidth
              className="btn-sm photo-delete icon-close confirm-modal-v2__btn-like"
              theme={Buttons.constants.BUTTON_THEMES.LINK}
            />
          </ConfirmModal>
        );
      }
    }

    return null;
  }

  renderStyleguideContent(photo) {
    const { styleguide, editable, onDelete } = this.props;
    if (styleguide) {
      if (photo.default && !editable) {
        return <span className="photo-container__default">Default *</span>;
      }
      if (onDelete && editable) {
        return (
          <ConfirmModal
            body="Are you sure you want to delete this photo?"
            confirmButtonText="Delete"
            containerClassName="photo-delete__wrapper"
            onConfirm={() => this.onDelete(photo.id)}
            title="Delete photo"
          >
            <Buttons.TextButton
              autoWidth
              className="btn-sm photo-delete icon-close confirm-modal-v2__btn-like"
              theme={Buttons.constants.BUTTON_THEMES.LINK}
            />
          </ConfirmModal>
        );
      }
    }
    return null;
  }

  renderPhoto(photo, index) {
    const { useLightbox } = this.props;
    const PhotoWrapperTag = useLightbox ? 'a' : 'div';
    const photoKey = _.get(photo, 'photo_key', null);

    if (!photoKey) {
      return null;
    }

    return (
      <div key={photo.id} className="photo-container">
        <PhotoWrapperTag
          className="thumbnail d-block"
          href="#photo"
          onClick={useLightbox ? e => this.onClickPhoto(e, index) : null}
        >
          <CloudinaryImage
            bucket={photo.bucket}
            photoKey={photoKey}
            options={{
              width: this.props.photoWidth,
              height: this.props.photoHeight,
              crop: this.props.crop,
            }}
          />
        </PhotoWrapperTag>
        {this.renderStyleguideContent(photo)}
        {this.renderEditable(photo)}
      </div>
    );
  }

  renderPreview(file, index) {
    const { onDeletePreview, photoHeight, photoWidth } = this.props;

    return (
      <div key={index} className="photo-container preview-container">
        <img
          className="preview-photo"
          src={file.preview}
          alt=""
          style={{
            maxWidth: photoWidth,
            maxHeight: photoHeight,
          }}
        />
        {onDeletePreview && (
          <ConfirmModal
            body="Are you sure you want to delete this photo?"
            confirmButtonText="Delete"
            containerClassName="photo-delete__wrapper"
            onConfirm={() => onDeletePreview(index)}
            title="Delete photo"
          >
            <Buttons.TextButton
              autoWidth
              className="btn-sm photo-delete icon-close confirm-modal-v2__btn-like"
              theme={Buttons.constants.BUTTON_THEMES.LINK}
            />
          </ConfirmModal>
        )}
      </div>
    );
  }

  render() {
    const {
      addPhotoText,
      children,
      flush,
      lightboxPhotoHeight,
      lightboxPhotoWidth,
      multiple,
      photos,
      previews,
      useLightbox,
      useUsualList,
    } = this.props;
    const filteredPhotosWithPhotoKey = _.filter(photos, photo => !_.isEmpty(photo.photo_key));

    return (
      <div className={classnames('photo-list', { flush })}>
        {useUsualList || isIE || isEdge
          ? _.map(photos, this.renderPhoto)
          : this.renderVirtualizedList(photos)}
        {_.map(previews, this.renderPreview)}
        {this.loadingPhoto()}
        {this.props.editable &&
          this.props.onDrop !== null &&
          this.props.isOnline &&
          (children || (
            <AddPhotoButton onDrop={this.props.onDrop} multiple={multiple}>
              {addPhotoText}
            </AddPhotoButton>
          ))}
        {useLightbox && (
          <Lightbox
            images={_.map(filteredPhotosWithPhotoKey, photo => ({
              src: getCloudinaryUrl(`${photo.bucket}/${photo.photo_key}`, {
                width: lightboxPhotoWidth,
                height: lightboxPhotoHeight,
                crop: 'limit',
              }),
              caption: (
                <a
                  href={`https://s3.amazonaws.com/${photo.bucket}/original/${photo.photo_key}`}
                  className="lightbox-original-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download original
                </a>
              ),
            }))}
            currentImage={this.state.currentPhotoIndex}
            isOpen={this.state.lightboxOpen}
            onClickPrev={this.goToPrevious}
            onClickNext={this.goToNext}
            onClose={this.closeLightbox}
            theme={lightboxTheme}
          />
        )}
      </div>
    );
  }
}

PhotoList.propTypes = {
  children: PropTypes.any,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      bucket: PropTypes.string,
      photo_key: PropTypes.string,
    }),
  ),

  addPhotoText: PropTypes.node,
  crop: PropTypes.oneOf(['crop', 'fit', 'pad', 'fill', 'scale', 'limit', 'lpad']),
  editable: PropTypes.bool,
  flush: PropTypes.bool,
  isOnline: PropTypes.bool,
  lightboxPhotoHeight: PropTypes.number,
  lightboxPhotoWidth: PropTypes.number,
  loadingPhotos: PropTypes.arrayOf(PropTypes.shape({})),
  multiple: PropTypes.bool,
  photoHeight: PropTypes.number,
  photoWidth: PropTypes.number,
  previews: PropTypes.arrayOf(PropTypes.shape({})),
  useLightbox: PropTypes.bool,

  importedEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDeletePreview: PropTypes.func,
  onDrop: PropTypes.func,
  styleguide: PropTypes.bool,
  useUsualList: PropTypes.bool,
};

PhotoList.defaultProps = {
  crop: 'limit',
  editable: true,
  flush: false,
  isOnline: true,
  lightboxPhotoHeight: 480,
  lightboxPhotoWidth: 720,
  multiple: true,
  onDelete: null,
  onDrop: null,
  photoHeight: 80,
  photos: [],
  photoWidth: 120,
  previews: [],
  styleguide: false,
  useLightbox: true,
  useUsualList: true,
};

export default PhotoList;
